import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CurrencyDollarIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  CreditCardIcon,
  BanknotesIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import ChangePasswordModal from "../components/ChangePasswordModal";
import logo from "../assests/images/logo.png";

function InvestorDashboardPage() {
  const [investorData, setInvestorData] = useState(null);
  const [contractData, setContractData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showChangePassword, setShowChangePassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInvestorData();
  }, []);

  const fetchInvestorData = async () => {
    try {
      setLoading(true);
      setError("");

      console.log("=== Starting fetchInvestorData ===");

      // Check if token exists
      const token = localStorage.getItem("token");
      console.log("Token exists:", !!token);

      if (!token) {
        console.log("No token found, redirecting to login");
        navigate("/investor-login");
        return;
      }

      // Test basic connectivity first
      console.log("Testing basic API connectivity...");
      try {
        const testResponse = await api.get("/api/auth/test");
        console.log("API test successful:", testResponse.data);
      } catch (testError) {
        console.error("API test failed:", testError);
        // Don't throw, continue with hardcoded data
        console.log("Continuing with hardcoded data...");
      }

      // FOR DEBUGGING: Set hardcoded data
      console.log("Setting hardcoded data for testing...");
      setInvestorData({
        buyer_name: "Karthi",
        mobile_number: "9876543210",
        wallet_address: "0xabcdef1234567890abcdef1234567890abcdef12",
        purchase_date: "2025-08-15",
        return_date: null,
        person_place: "Kumarapalayam",
        referred_by: "Bob Wilson",
        referrer_wallet: "0xfedcba0987654321fedcba0987654321fedcba09",
        status: "With UNS",
        token_number: null,
        scheme_name: "10$ Account",
      });
      setContractData([]);
      console.log("Hardcoded data set successfully!");

      /* COMMENTED OUT FOR DEBUGGING
      // Try to get investor profile
      console.log("Fetching investor profile...");
      try {
        const investorRes = await api.get("/api/auth/investor-profile");
        console.log("Investor profile success:", investorRes.data);
        setInvestorData(investorRes.data);
      } catch (profileError) {
        console.error("Profile fetch failed:", profileError);
        if (profileError.response?.status === 401) {
          console.log("Unauthorized, clearing token and redirecting");
          localStorage.removeItem("token");
          navigate("/investor-login");
          return;
        }
        throw profileError;
      }

      // Try to get contracts (non-critical)
      console.log("Fetching investor contracts...");
      try {
        const contractRes = await api.get("/api/auth/investor-contracts");
        console.log("Contracts success:", contractRes.data);
        setContractData(contractRes.data || []);
      } catch (contractError) {
        console.warn("Contracts fetch failed, continuing with empty array:", contractError);
        setContractData([]);
      }
      */

      console.log("=== Data fetch completed successfully ===");
    } catch (error) {
      console.error("=== fetchInvestorData failed ===", error);

      if (error.response) {
        console.error("Error response:", {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        if (error.response.status === 401) {
          console.log("Unauthorized - redirecting to login");
          localStorage.removeItem("token");
          navigate("/investor-login");
          return;
        }

        setError(
          `Server error: ${
            error.response.data?.message || `HTTP ${error.response.status}`
          }`
        );
      } else if (error.request) {
        console.error("No response received:", error.request);
        setError(
          "Cannot connect to server. Please check if the backend is running on port 5001."
        );
      } else {
        console.error("Request setup error:", error.message);
        setError(`Request error: ${error.message}`);
      }
    } finally {
      setLoading(false);
      console.log("=== fetchInvestorData finished ===");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/investor-login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          <p className="mt-2 text-sm text-gray-500">
            Please check console for debugging info
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="text-red-600 text-lg font-medium mb-4">{error}</div>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!investorData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-600">No investor data found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo and Brand */}
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <img
                    src={logo}
                    alt="Investor Portal Logo"
                    className="w-20 rounded-lg object-cover"
                    style={{ backgroundColor: "white", padding: "2px" }}
                  />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">
                    Investor Portal
                  </h1>
                  <p className="text-sm text-gray-500">Welcome back</p>
                </div>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    {investorData?.buyer_name || "Investor"}
                  </p>
                  <p className="text-gray-500">
                    {investorData?.mobile_number || ""}
                  </p>
                </div>
              </div>

              <button
                onClick={() => setShowChangePassword(true)}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <Cog6ToothIcon className="h-4 w-4 mr-2" />
                Settings
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 rounded-md text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">
            Welcome, {investorData.buyer_name}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Here's an overview of your investment portfolio
          </p>
        </div>

        {/* Investment Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <DocumentTextIcon className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Investment Scheme
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  {investorData.scheme_name || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Purchase Date
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  {investorData.purchase_date
                    ? new Date(investorData.purchase_date).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center">
                  <CreditCardIcon className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Status</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {investorData.status || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center">
                  <BanknotesIcon className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">
                  Total Transactions
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  {contractData.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Personal Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Full Name
                    </label>
                    <p className="text-sm text-gray-900">
                      {investorData.buyer_name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Mobile Number
                    </label>
                    <p className="text-sm text-gray-900">
                      {investorData.mobile_number || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Location
                    </label>
                    <p className="text-sm text-gray-900">
                      {investorData.person_place || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Referred By
                    </label>
                    <p className="text-sm text-gray-900">
                      {investorData.referred_by || "N/A"}
                    </p>
                  </div>
                </div>
                {investorData.token_number && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Token Number
                    </label>
                    <p className="text-sm text-gray-900">
                      {investorData.token_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Wallet Information */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Wallet Information
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Your Wallet Address
                  </label>
                  <p className="text-sm text-gray-900 break-all font-mono bg-gray-50 p-2 rounded">
                    {investorData.wallet_address || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Referrer Wallet
                  </label>
                  <p className="text-sm text-gray-900 break-all font-mono bg-gray-50 p-2 rounded">
                    {investorData.referrer_wallet || "N/A"}
                  </p>
                </div>
                {investorData.return_date && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Return Date
                    </label>
                    <p className="text-sm text-gray-900">
                      {new Date(investorData.return_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Transaction History
              </h3>
              <span className="text-sm text-gray-500">
                {contractData.length} transactions
              </span>
            </div>

            {contractData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction Hash
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From Wallet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        To Wallet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount (USDT)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contractData.map((transaction, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="text-sm font-mono text-gray-900 truncate block max-w-32"
                            title={transaction.transaction_hash}
                          >
                            {transaction.transaction_hash
                              ? `${transaction.transaction_hash.slice(
                                  0,
                                  10
                                )}...${transaction.transaction_hash.slice(-6)}`
                              : "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.transaction_date || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="text-sm font-mono text-gray-600 truncate block max-w-32"
                            title={transaction.from_wallet_address}
                          >
                            {transaction.from_wallet_address
                              ? `${transaction.from_wallet_address.slice(
                                  0,
                                  8
                                )}...${transaction.from_wallet_address.slice(
                                  -4
                                )}`
                              : "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="text-sm font-mono text-gray-600 truncate block max-w-32"
                            title={transaction.to_wallet_address}
                          >
                            {transaction.to_wallet_address
                              ? `${transaction.to_wallet_address.slice(
                                  0,
                                  8
                                )}...${transaction.to_wallet_address.slice(-4)}`
                              : "N/A"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {transaction.usdt_received
                            ? `$${parseFloat(
                                transaction.usdt_received
                              ).toLocaleString()}`
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              investorData.status === "With UNS"
                                ? "bg-green-100 text-green-800"
                                : "bg-purple-100 text-purple-800"
                            }`}
                          >
                            {investorData.status || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h4 className="mt-2 text-sm font-medium text-gray-900">
                  No transactions found
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Your transaction history will appear here once available.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userType="investor"
      />
    </div>
  );
}

export default InvestorDashboardPage;
